import React, { useState, useEffect, RefObject } from 'react'
import { Popup } from 'semantic-ui-react'
import { filters, FilterSchema } from '@adc/blueprint'
import FilterButton from './filterButton'
import FilterPanel from './filterPanel'
import FilterGroupLayout from './filterGroupLayout'
import classnames from 'classnames/bind'
import styles from './styles.scss'
import type { ButtonWidth, PanelHeight } from './types'
import Drawer from '../../overlays/drawer'
import EnhancedDrawer from '../../overlays/enhancedDrawer'
const cx = classnames.bind(styles)

export type FilterProps = {
  buttonWidth?: ButtonWidth
  data: FilterSchema.FilterGroupViewModel
  defaultButtonLabel?: string // optional hard-coded label
  definition: FilterSchema.FilterGroupDefinition
  elmId?: string
  filterButtonRef?: RefObject<HTMLButtonElement>
  isLoading?: boolean
  onChange: (key: string, value: FilterSchema.ValueType) => void
  onReset: () => void
  panelHeight?: PanelHeight
  resultsAmount?: number
  size?: 'small' | 'medium'
  useEnhancedDrawer?: boolean
  variant?: 'default' | 'pill' | 'floating'
}

/**
 * Filter: a filter component that renders one filter group (e.g. Property filter).
 *
 * @definition group definition
 * @data group data
 *
 * @returns
 */
const Filter: React.FunctionComponent<FilterProps> = ({
  buttonWidth,
  data,
  defaultButtonLabel,
  definition,
  elmId = 'filter',
  filterButtonRef,
  isLoading = false,
  onChange,
  onReset,
  panelHeight,
  resultsAmount = 0,
  size = 'medium',
  useEnhancedDrawer = false,
  variant = 'default',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isFilterSelected, setIsFilterSelected] = useState(false) // as in, at least one filter is in use
  const [filterNotSelectedButtonLabel, setFilterNotSelectedButtonLabel] =
    useState(defaultButtonLabel || definition.label)

  const [buttonLabel, setButtonLabel] = useState(filterNotSelectedButtonLabel)

  useEffect(() => {
    setIsFilterSelected(!filters.utils.isGroupFilterReset(data))
    setButtonLabel(filterNotSelectedButtonLabel)
  }, [data, filterNotSelectedButtonLabel])

  useEffect(() => {
    setFilterNotSelectedButtonLabel(defaultButtonLabel || definition.label)
  }, [defaultButtonLabel, definition])

  useEffect(() => {
    // Function to update the label
    const updateLabel = () => {
      setButtonLabel(filters.utils.getLabelFromGroup(data, definition))
    }

    updateLabel()

    // For async filters, check if we need to wait for labels to be cached
    const checkAsyncLabels = async () => {
      const hasAsyncFilters = definition.items.some(item => {
        if (item.itemType === 'filter') {
          const filter = item as FilterSchema.FilterDefinition
          return (
            filter.controlType === 'singleSelect' &&
            (filter.lookupMode === 'async' || filter.lookupMode === 'asyncOnce')
          )
        } else if (item.itemType === 'section') {
          // Check filters within sections
          const section = item as FilterSchema.FilterSectionDefinition
          return section.filters?.some(
            filter =>
              filter.controlType === 'singleSelect' &&
              (filter.lookupMode === 'async' ||
                filter.lookupMode === 'asyncOnce')
          )
        }
        return false
      })

      if (hasAsyncFilters) {
        // Wait a bit for cache to update, then update label again
        await filters.utils.updateAsyncFilterLabelCache([definition], {
          [definition.name]: data,
        })
        updateLabel()
      }
    }

    checkAsyncLabels().catch(console.error)
  }, [data, definition])

  const buttonIcon =
    variant === 'pill' || variant === 'floating'
      ? undefined
      : definition.labelIconProps?.name

  const DrawerComponent = useEnhancedDrawer ? EnhancedDrawer : Drawer
  const drawerProps = useEnhancedDrawer
    ? {
        mobileLayout: 'fullscreen' as const,
        pushBehavior: 'overlay' as const,
        keepMounted: false,
      }
    : { headerStyle: 'clear', isWide: true, noScroll: true, title: '' }

  return definition.mode === 'completeFilter' ? (
    <>
      <FilterButton
        filterButtonRef={filterButtonRef}
        icon={buttonIcon}
        isFilterSelected={isFilterSelected}
        onOpen={setIsOpen}
        isOpen={isOpen}
        onReset={onReset}
        label={buttonLabel}
        width={buttonWidth}
        elmId={`${elmId}_button`}
        size={size}
        variant={variant}
      />
      <DrawerComponent
        elmId={`${elmId}_drawer`}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        {...drawerProps}
      >
        <FilterPanel
          elmId={`${elmId}_panel`}
          isDrawer
          isEnhancedDrawer={useEnhancedDrawer}
          isLoading={isLoading}
          onOpen={setIsOpen}
          onReset={onReset}
          resultsAmount={resultsAmount}
        >
          <FilterGroupLayout
            key={definition.name}
            groupDefinition={definition}
            groupData={data}
            onChange={onChange}
            height={panelHeight}
          />
        </FilterPanel>
      </DrawerComponent>
    </>
  ) : (
    <Popup
      content={
        <FilterPanel
          elmId={`${elmId}_panel`}
          onOpen={setIsOpen}
          onReset={onReset}
        >
          <FilterGroupLayout
            key={definition.name}
            groupDefinition={definition}
            groupData={data}
            onChange={onChange}
            height={panelHeight}
          />
        </FilterPanel>
      }
      on='click'
      open={isOpen}
      onClose={() => setIsOpen(false)}
      onOpen={() => setIsOpen(true)}
      className={cx('filter-popup')}
      trigger={
        <FilterButton
          filterButtonRef={filterButtonRef}
          icon={buttonIcon}
          isFilterSelected={isFilterSelected}
          onOpen={setIsOpen}
          isOpen={isOpen}
          onReset={onReset}
          label={buttonLabel}
          width={buttonWidth}
          elmId={`${elmId}_button`}
          size={size}
          variant={variant}
        />
      }
      position='bottom left'
    />
  )
}

export default Filter
